import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useHousingData, DISTRICT_VI_MAP } from '../hooks/useHousingData';
import { PROPERTY_TYPES, AMENITIES_LIST } from '../constants';
import HousingCard from '../components/HousingCard';
import SkeletonCard from '../components/SkeletonCard';

// Strip Vietnamese diacritics for accent-insensitive search
const removeAccents = (str) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

// Price bucket definition: [min, max, i18n key]
const PRICE_BUCKETS = [
  { key: 'lt500k', min: 0, max: 500000, labelKey: 'filter.lessThan500k' },
  { key: '500k-1m', min: 500000, max: 1000000, labelKey: 'facets.price500k1m' },
  { key: '1m-2m', min: 1000000, max: 2000000, labelKey: 'facets.price1m2m' },
  { key: '2m-3m', min: 2000000, max: 3000000, labelKey: 'facets.price2m3m' },
  { key: '3m-5m', min: 3000000, max: 5000000, labelKey: 'facets.price3m5m' },
  { key: 'gt5m', min: 5000000, max: Infinity, labelKey: 'facets.priceGt5m' },
];

const FacetSection = ({ title, children }) => (
  <div className="border-b border-gray-100 pb-5 mb-5 last:border-b-0 last:pb-0 last:mb-0">
    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">{title}</p>
    {children}
  </div>
);

const FacetOption = ({ label, count, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-sm transition-all border-2 ${
      active
        ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium'
        : 'border-transparent bg-gray-50 text-gray-700 hover:bg-gray-100'
    }`}
  >
    <span className="flex items-center gap-2 truncate">
      <span className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
        active ? 'border-primary-500 bg-primary-500' : 'border-gray-300'
      }`}>
        {active && (
          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        )}
      </span>
      <span className="truncate">{label}</span>
    </span>
    <span className={`text-xs flex-shrink-0 ml-2 ${active ? 'text-primary-600' : 'text-gray-400'}`}>
      {count ?? 0}
    </span>
  </button>
);

const PropertyFacetsPage = () => {
  const { t, i18n } = useTranslation();
  const { properties, loading, dataSource, refresh, error } = useHousingData();
  const [searchParams, setSearchParams] = useSearchParams();
  const isVietnamese = i18n.language === 'vi';

  // ---- Facet selections from URL ----
  const selectedDistricts = useMemo(
    () => (searchParams.get('districts') || '').split(',').filter(Boolean),
    [searchParams]
  );
  const selectedTypes = useMemo(
    () => (searchParams.get('types') || '').split(',').filter(Boolean),
    [searchParams]
  );
  const selectedPrices = useMemo(
    () => (searchParams.get('prices') || '').split(',').filter(Boolean),
    [searchParams]
  );
  const selectedAmenities = useMemo(
    () => (searchParams.get('amenities') || '').split(',').filter(Boolean),
    [searchParams]
  );
  const onlyAvailable = searchParams.get('available') === '1';
  const searchQuery = searchParams.get('q') || '';
  const page = Math.max(parseInt(searchParams.get('page') || '1', 10) || 1, 1);
  const perPage = parseInt(searchParams.get('perPage') || '50', 10) || 50;

  // Debounced keyword input: local state for smooth typing,
  // URL filter applied 1s after the last keystroke
  const [keywordInput, setKeywordInput] = useState(searchQuery);
  const debounceRef = useRef(null);
  useEffect(() => {
    setKeywordInput(searchQuery);
  }, [searchQuery]);
  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);
  const handleKeywordChange = (value) => {
    setKeywordInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setParam('q', value);
    }, 1000);
  };

  const hasActiveFilters =
    selectedDistricts.length > 0 || selectedTypes.length > 0 ||
    selectedPrices.length > 0 || selectedAmenities.length > 0 ||
    onlyAvailable || !!searchQuery;

  const setParam = (key, value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      return next;
    });
  };

  const toggleListParam = (key, currentList, value) => {
    const next = currentList.includes(value)
      ? currentList.filter((v) => v !== value)
      : [...currentList, value];
    setParam(key, next.join(','));
  };

  const resetFilters = () => setSearchParams({});
  const setPage = (value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value > 1) next.set('page', String(value));
      else next.delete('page');
      return next;
    });
  };
  const setPerPage = (value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('perPage', String(value));
      next.delete('page');
      return next;
    });
  };

  // ---- Filtered results ----
  const filtered = useMemo(() => {
    return properties.filter((item) => {
      if (onlyAvailable && item.available === false) return false;

      if (searchQuery) {
        const q = removeAccents(searchQuery.toLowerCase());
        const haystack = [
          item.title, item.titleVi, item.district, item.districtVi,
          item.city, item.cityVi,
        ].filter(Boolean).map((s) => removeAccents(s.toLowerCase()));
        if (!haystack.some((s) => s.includes(q))) return false;
      }

      if (selectedDistricts.length > 0) {
        const itemDistrict = isVietnamese
          ? (item.districtVi || DISTRICT_VI_MAP[item.district] || item.district)
          : item.district;
        if (!selectedDistricts.includes(itemDistrict)) return false;
      }

      if (selectedTypes.length > 0) {
        if (!selectedTypes.includes((item.type || '').normalize('NFC'))) return false;
      }

      if (selectedPrices.length > 0) {
        const inBucket = selectedPrices.some((bucketKey) => {
          const bucket = PRICE_BUCKETS.find((b) => b.key === bucketKey);
          return bucket && item.price >= bucket.min && item.price < bucket.max;
        });
        if (!inBucket) return false;
      }

      if (selectedAmenities.length > 0) {
        const hasAll = selectedAmenities.every((a) => item.amenities.includes(a));
        if (!hasAll) return false;
      }

      return true;
    });
  }, [properties, selectedDistricts, selectedTypes, selectedPrices, selectedAmenities, onlyAvailable, searchQuery, isVietnamese]);

  // ---- Facet counts (computed on the set filtered by everything EXCEPT that facet) ----
  const computeCounts = (predicate, keyExtractor) => {
    const counts = {};
    for (const p of properties) {
      if (!predicate(p)) continue;
      const val = keyExtractor(p);
      if (val != null) counts[val] = (counts[val] || 0) + 1;
    }
    return counts;
  };

  const matchesAllExcept = (except) => (item) => {
    if (onlyAvailable && item.available === false) return false;
    if (searchQuery) {
      const q = removeAccents(searchQuery.toLowerCase());
      const haystack = [
        item.title, item.titleVi, item.district, item.districtVi,
        item.city, item.cityVi,
      ].filter(Boolean).map((s) => removeAccents(s.toLowerCase()));
      if (!haystack.some((s) => s.includes(q))) return false;
    }
    if (except !== 'district' && selectedDistricts.length > 0) {
      const itemDistrict = isVietnamese
        ? (item.districtVi || DISTRICT_VI_MAP[item.district] || item.district)
        : item.district;
      if (!selectedDistricts.includes(itemDistrict)) return false;
    }
    if (except !== 'type' && selectedTypes.length > 0) {
      if (!selectedTypes.includes((item.type || '').normalize('NFC'))) return false;
    }
    if (except !== 'price' && selectedPrices.length > 0) {
      const inBucket = selectedPrices.some((bucketKey) => {
        const bucket = PRICE_BUCKETS.find((b) => b.key === bucketKey);
        return bucket && item.price >= bucket.min && item.price < bucket.max;
      });
      if (!inBucket) return false;
    }
    if (except !== 'amenity' && selectedAmenities.length > 0) {
      if (!selectedAmenities.every((a) => item.amenities.includes(a))) return false;
    }
    return true;
  };

  const districtCounts = useMemo(() => computeCounts(
    matchesAllExcept('district'),
    (p) => (isVietnamese
      ? (p.districtVi || DISTRICT_VI_MAP[p.district] || p.district)
      : p.district)
  ), [properties, selectedTypes, selectedPrices, selectedAmenities, onlyAvailable, searchQuery, isVietnamese]);

  const typeCounts = useMemo(() => computeCounts(
    matchesAllExcept('type'),
    (p) => (p.type || '').normalize('NFC')
  ), [properties, selectedDistricts, selectedPrices, selectedAmenities, onlyAvailable, searchQuery, isVietnamese]);

  const priceCounts = useMemo(() => computeCounts(
    matchesAllExcept('price'),
    (p) => (PRICE_BUCKETS.find((b) => p.price >= b.min && p.price < b.max) || {}).key
  ), [properties, selectedDistricts, selectedTypes, selectedAmenities, onlyAvailable, searchQuery, isVietnamese]);

  const amenityCounts = useMemo(() => {
    const counts = {};
    const predicate = matchesAllExcept('amenity');
    for (const p of properties) {
      if (!predicate(p)) continue;
      for (const a of p.amenities || []) {
        if (AMENITIES_LIST.some((am) => am.key === a)) {
          counts[a] = (counts[a] || 0) + 1;
        }
      }
    }
    return counts;
  }, [properties, selectedDistricts, selectedTypes, selectedPrices, onlyAvailable, searchQuery, isVietnamese]);

  const availableCount = useMemo(() => {
    const predicate = matchesAllExcept('available');
    return properties.filter((p) => p.available !== false && predicate(p)).length;
  }, [properties, selectedDistricts, selectedTypes, selectedPrices, selectedAmenities, searchQuery, isVietnamese]);

  // ---- Pagination ----
  const totalPages = Math.max(Math.ceil(filtered.length / perPage), 1);
  const safePage = Math.min(page, totalPages);
  const paginated = useMemo(
    () => filtered.slice((safePage - 1) * perPage, safePage * perPage),
    [filtered, safePage, perPage]
  );

  // Reset to page 1 whenever filters actually change (not when page itself changes)
  const filtersSignature = [
    selectedDistricts.join(','),
    selectedTypes.join(','),
    selectedPrices.join(','),
    selectedAmenities.join(','),
    onlyAvailable,
    searchQuery,
  ].join('|');
  const prevFiltersRef = useRef(filtersSignature);
  useEffect(() => {
    if (prevFiltersRef.current !== filtersSignature) {
      prevFiltersRef.current = filtersSignature;
      if (searchParams.get('page')) {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete('page');
          return next;
        });
      }
    }
  }, [filtersSignature, searchParams, setSearchParams]);

  // ---- Facet option lists ----
  const districtOptions = useMemo(() => {
    const set = new Set();
    for (const p of properties) {
      set.add(isVietnamese
        ? (p.districtVi || DISTRICT_VI_MAP[p.district] || p.district)
        : p.district);
    }
    return [...set].filter(Boolean).sort((a, b) => a.localeCompare(b, isVietnamese ? 'vi' : 'en'));
  }, [properties, isVietnamese]);

  const showLoadingScreen = loading && dataSource !== 'google_drive' && properties.length === 0;

  if (showLoadingScreen) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <span>🔎</span>
            <span>{t('facets.title')}</span>
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {t('facets.subtitle')}
          </p>
        </div>
        {hasActiveFilters && (
          <button onClick={resetFilters} className="btn-outline text-sm px-4 py-2 whitespace-nowrap">
            {t('filter.reset')}
          </button>
        )}
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Facet Sidebar */}
        <aside className="lg:w-72 flex-shrink-0 lg:sticky lg:top-24 lg:h-[calc(100vh-6rem)]">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 lg:h-full lg:overflow-y-auto">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <svg className="w-5 h-5 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <span>{t('filter.title')}</span>
            </h3>

            {/* Keyword search */}
            <FacetSection title={t('facets.keyword')}>
              <div className="flex items-center bg-gray-50 rounded-xl px-3 border-2 border-transparent focus-within:border-primary-300 transition-colors">
                <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={keywordInput}
                  onChange={(e) => handleKeywordChange(e.target.value)}
                  placeholder={t('hero.searchPlaceholder')}
                  className="w-full px-2 py-2 text-sm bg-transparent border-none focus:outline-none"
                />
              </div>
            </FacetSection>

            {/* Availability facet */}
            <FacetSection title={t('facets.availability')}>
              <FacetOption
                label={t('detail.available')}
                count={availableCount}
                active={onlyAvailable}
                onClick={() => setParam('available', onlyAvailable ? '' : '1')}
              />
            </FacetSection>

            {/* District facet */}
            <FacetSection title={t('filter.location')}>
              <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                {districtOptions.map((d) => (
                  <FacetOption
                    key={d}
                    label={d}
                    count={districtCounts[d]}
                    active={selectedDistricts.includes(d)}
                    onClick={() => toggleListParam('districts', selectedDistricts, d)}
                  />
                ))}
              </div>
            </FacetSection>

            {/* Type facet */}
            <FacetSection title={t('filter.type')}>
              <div className="space-y-1.5">
                {PROPERTY_TYPES.map((type) => (
                  <FacetOption
                    key={type.value}
                    label={t(type.labelKey)}
                    count={typeCounts[type.value.normalize('NFC')]}
                    active={selectedTypes.includes(type.value)}
                    onClick={() => toggleListParam('types', selectedTypes, type.value)}
                  />
                ))}
              </div>
            </FacetSection>

            {/* Price facet */}
            <FacetSection title={t('filter.priceRange')}>
              <div className="space-y-1.5">
                {PRICE_BUCKETS.map((bucket) => (
                  <FacetOption
                    key={bucket.key}
                    label={t(bucket.labelKey)}
                    count={priceCounts[bucket.key]}
                    active={selectedPrices.includes(bucket.key)}
                    onClick={() => toggleListParam('prices', selectedPrices, bucket.key)}
                  />
                ))}
              </div>
            </FacetSection>

            {/* Amenities facet */}
            <FacetSection title={t('detail.amenities')}>
              <div className="flex flex-wrap gap-2">
                {AMENITIES_LIST.map((amenity) => {
                  const active = selectedAmenities.includes(amenity.key);
                  const count = amenityCounts[amenity.key] || 0;
                  return (
                    <button
                      key={amenity.key}
                      type="button"
                      onClick={() => toggleListParam('amenities', selectedAmenities, amenity.key)}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border-2 text-xs font-medium transition-all ${
                        active
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      } ${count === 0 && !active ? 'opacity-50' : ''}`}
                    >
                      <span>{amenity.icon}</span>
                      <span>{t(`amenity.${amenity.key}`)}</span>
                      <span className={active ? 'text-primary-600' : 'text-gray-400'}>{count}</span>
                    </button>
                  );
                })}
              </div>
            </FacetSection>
          </div>
        </aside>

        {/* Results */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <p className="text-sm text-gray-500">
              {t('facets.results', { count: filtered.length })}
            </p>
            {/* Page size selector */}
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span>{t('facets.perPage')}</span>
              <select
                value={perPage}
                onChange={(e) => setPerPage(parseInt(e.target.value, 10))}
                className="input-field text-sm py-1.5 w-auto"
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          </div>

          {paginated.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {paginated.map((housing) => (
                <HousingCard key={housing.id} housing={housing} />
              ))}
              {loading && dataSource === 'google_drive' && Array.from({ length: Math.max(6 - paginated.length, 3) }).map((_, i) => (
                <SkeletonCard key={`skeleton-${i}`} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16" data-testid="facets-no-results">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">{t('listing.noResults')}</h3>
              <p className="text-sm text-gray-500">{t('listing.noResultsHint')}</p>
            </div>
          )}

          {/* Pagination controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8 flex-wrap">
              <button
                type="button"
                onClick={() => setPage(safePage - 1)}
                disabled={safePage <= 1}
                className="btn-outline text-sm px-3 py-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                ← {t('facets.prevPage')}
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((n) =>
                  n === 1 || n === totalPages ||
                  Math.abs(n - safePage) <= 1
                )
                .map((n, idx, arr) => (
                  <React.Fragment key={n}>
                    {idx > 0 && n - arr[idx - 1] > 1 && (
                      <span className="text-gray-400 text-sm px-1">…</span>
                    )}
                    <button
                      type="button"
                      onClick={() => setPage(n)}
                      className={`w-9 h-9 rounded-lg text-sm font-medium transition-all ${
                        n === safePage
                          ? 'bg-primary-500 text-white shadow'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {n}
                    </button>
                  </React.Fragment>
                ))}
              <button
                type="button"
                onClick={() => setPage(safePage + 1)}
                disabled={safePage >= totalPages}
                className="btn-outline text-sm px-3 py-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {t('facets.nextPage')} →
              </button>
              <span className="text-sm text-gray-400 ml-2">
                {t('facets.pageInfo', { current: safePage, total: totalPages })}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="mt-6 bg-red-50 border border-red-200 text-red-700 rounded-xl px-5 py-3 flex items-center justify-between">
          <p className="text-sm">{t('common.error')}: {error}</p>
          <button onClick={refresh} className="text-sm font-medium text-red-700 hover:text-red-900 underline ml-4">
            {t('common.retry')}
          </button>
        </div>
      )}
    </div>
  );
};

export default PropertyFacetsPage;